import {
	ChangeDetectionStrategy,
	Component,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { Logger } from 'src/app/shared/core/logger';

import { ESigningRecipientListComponent } from '../../common/e-signing-recipient-list/e-signing-recipient-list.component';
import { ModalService } from '@ui/modal/modal.service';
import { AVATAR_VARIANT_ORDER } from '@ui/utils/constants';
import { ClientSigningDetailsViewModelComplete } from '@models/e-signing';
import { MODAL_DATA } from '@ui/modal/modal-data.token';
import { EnvelopeService } from '@services/envelope/envelope.service';

interface ESigningEditRecipientModalData {
	onPrimaryActionSuccess?: () => void;
	submitted?: boolean;
	envelopeId: string;
}

@UntilDestroy()
@Component({
	selector: 'app-e-signing-edit-recipient-modal-view',
	imports: [ESigningRecipientListComponent, ReactiveFormsModule, AsyncPipe],
	templateUrl: './e-signing-edit-recipient-modal-view.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'w-full' },
})
export class ESigningEditRecipientModalViewComponent implements OnInit {
	AVATAR_VARIANT_ORDER = AVATAR_VARIANT_ORDER;

	private envelopeService = inject(EnvelopeService);
	private modalService = inject(ModalService);
	public submitted_ = signal<boolean>(false);
	public form: ReturnType<EnvelopeService['getRecipientForm']>['form'];
	public recipients: ClientSigningDetailsViewModelComplete[] = [];
	public MODAL_DATA = inject<ESigningEditRecipientModalData>(MODAL_DATA);
	public title$: Observable<string>;

	ngOnInit() {
		if (this.MODAL_DATA.submitted) {
			this.submitted_.set(this.MODAL_DATA.submitted);
		}

		const { form, recipients } = this.envelopeService.getRecipientForm(
			this.MODAL_DATA.envelopeId,
		);

		this.form = form;
		this.recipients = recipients;
		this.title$ = this.envelopeService.getSignersCount$(this.form);

		this.handleValueChanges();
		this.setPrimaryAction();
	}

	private handleValueChanges() {
		this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
			Object.keys(value).forEach((key) => {
				const isParticipatingControl = this.form
					.get(key)
					.get('isParticipating');
				const emailControl = this.form.get(key).get('email');
				if (isParticipatingControl) {
					if (value[key].isParticipating) {
						emailControl.setValidators([Validators.required, Validators.email]);
					} else {
						emailControl.clearValidators();
					}
					emailControl.updateValueAndValidity({ emitEvent: false });
				}
			});
		});
	}

	private setPrimaryAction() {
		this.modalService.setPrimaryAction('Save', async () => {
			this.modalService.setPrimaryActionLoading(true);

			try {
				if (this.form.invalid) {
					this.submitted_.set(false);
					return;
				}
				this.submitted_.set(true);

				const rawValues = this.form.getRawValue();

				await Promise.all([
					this.envelopeService.saveRecipientsEmail(
						Object.entries(rawValues).map(
							([recipientId, { email, participantType }]) => ({
								recipientId,
								email,
								participantType,
							}),
						),
					),
					this.envelopeService.updateRecipientParticipation(
						this.MODAL_DATA.envelopeId,
						Object.entries(rawValues).map(
							([clientId, { isParticipating, participantType }]) => ({
								clientId,
								isParticipating,
								participantType,
							}),
						),
					),
				]);

				this.MODAL_DATA.onPrimaryActionSuccess
					? this.MODAL_DATA.onPrimaryActionSuccess()
					: this.modalService.closeModal();
			} catch (error) {
				Logger.error(`Error saving recipient details: ${error}`);
			} finally {
				this.modalService.setPrimaryActionLoading(false);
			}
		});
	}
}
